import { useState } from 'react';
import { api, extractErrorMessage } from '../api/client';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { MotivosMovimientoStock } from './mantenimiento/MotivosMovimientoStock';
import { DatosPrueba } from './mantenimiento/DatosPrueba';
import { useToast } from '../components/Toast';
import { DataTable } from '../components/DataTable';
import { Button } from '../components/Button';
import { Confirm } from '../components/Confirm';

/**
 * GET remitos/sin-ingreso-stock: remito con OC cuya recepción del depósito falló ('error') o se cortó sin terminar
 * ('a_medias', sin cambios hace más de 10 minutos). Sin datos monetarios.
 */
export interface RemitoSinIngreso {
  id: number;
  numeroRemito: string | null;
  /** AAAA-MM-DD */
  fecha: string;
  estado: string;
  proveedor: { id: number; nombre: string } | null;
  ordenCompra: { id: number; numeroOrden: string | null };
  situacion: 'error' | 'a_medias';
  wmsError: string | null;
  cantidadItems: number;
  unidades: number;
  recepcionesEnBorrador: { id: number; fecha: string }[];
}

/** GET remitos/recepciones-en-borrador: recepción sin confirmar que dejó la sincronización de un remito. */
export interface RecepcionEnBorrador {
  id: number;
  estado: string;
  /** AAAA-MM-DD */
  fecha: string;
  creada: string | null;
  numeroRemito: string | null;
  numeroOrden: string | null;
  proveedorNombre: string | null;
  cantidadItems: number;
  unidades: number;
  remitoId: number;
  remito: { id: number; numeroRemito: string | null; wmsStatus: string | null } | null;
}

interface ResultadoReintento {
  id: number;
  wmsStatus: string | null;
  wmsError: string | null;
  wmsReceiptId: number | null;
  recepcionesDescartadas: number;
}

const URL_REMITOS = '/api/v3/remitos/sin-ingreso-stock';
const URL_RECEPCIONES = '/api/v3/remitos/recepciones-en-borrador';
const SIN_PERMISO = 'No tenés permiso para realizar esta acción';

/** AAAA-MM-DD → DD/MM/AAAA, sin pasar por Date (evita el corrimiento de un día por la zona horaria). */
const fechaCorta = (iso: string) => iso.slice(0, 10).split('-').reverse().join('/');
const nombreRemito = (numero: string | null | undefined, id: number) => numero || `#${id}`;

/** Estado de stock del remito de una recepción en borrador. */
function estadoRemito(r: RecepcionEnBorrador): string {
  if (!r.remito) return 'Remito eliminado';
  switch (r.remito.wmsStatus) {
    case 'ok': return 'Ya ingresó al stock';
    case 'error': return 'Pendiente de ingresar';
    case 'syncing': return 'Sincronizando';
    default: return r.remito.wmsStatus ?? 'Sin estado';
  }
}

function Seccion({ id, titulo, descripcion, children }: { id: string; titulo: string; descripcion: string; children: React.ReactNode }) {
  return (
    <section aria-labelledby={id} className="space-y-2">
      <div>
        <h2 id={id} className="text-lg font-semibold text-slate-800">{titulo}</h2>
        <p className="text-slate-500 text-sm">{descripcion}</p>
      </div>
      {children}
    </section>
  );
}

export function MantenimientoPage() {
  const { can, user } = useAuth();
  const { addToast } = useToast();
  const puedeEditar = can('remitos', 'editar');
  // La página se abre con remitos:ver o wms:ver; cada sección se muestra (y pide sus datos) sólo con su permiso.
  const verRemitos = can('remitos', 'ver');
  const verMotivos = can('wms', 'ver');
  const remitos = useApi<RemitoSinIngreso[]>(verRemitos ? URL_REMITOS : null);
  const recepciones = useApi<RecepcionEnBorrador[]>(verRemitos ? URL_RECEPCIONES : null);
  const [aReintentar, setAReintentar] = useState<RemitoSinIngreso | null>(null);
  const [aDescartar, setADescartar] = useState<RecepcionEnBorrador | null>(null);
  const [procesando, setProcesando] = useState(false);

  const recargar = () => { remitos.refetch(); recepciones.refetch(); };

  const reintentar = async () => {
    const remito = aReintentar;
    if (!remito) return;
    setAReintentar(null);
    setProcesando(true);
    const nombre = nombreRemito(remito.numeroRemito, remito.id);
    try {
      const res = await api.post<ResultadoReintento>(`/api/v3/remitos/${remito.id}/reintentar-ingreso`, {});
      if (res.wmsStatus === 'ok') {
        addToast(`El remito ${nombre} ingresó al stock.`, 'success');
      } else {
        addToast(`El remito ${nombre} no pudo ingresar al stock: ${res.wmsError ?? 'error desconocido'}`, 'error');
      }
    } catch (e) {
      addToast(extractErrorMessage(e), 'error');
    } finally {
      setProcesando(false);
      recargar();
    }
  };

  const descartar = async () => {
    const recepcion = aDescartar;
    if (!recepcion) return;
    setADescartar(null);
    setProcesando(true);
    try {
      await api.post(`/api/v3/remitos/recepciones-en-borrador/${recepcion.id}/descartar`, {});
      addToast(`Se descartó la recepción en borrador #${recepcion.id}.`, 'success');
    } catch (e) {
      addToast(extractErrorMessage(e), 'error');
    } finally {
      setProcesando(false);
      recargar();
    }
  };

  const columnasRemitos = [
    { key: 'numeroRemito', header: 'Remito', render: (r: RemitoSinIngreso) => nombreRemito(r.numeroRemito, r.id) },
    { key: 'proveedor', header: 'Proveedor', render: (r: RemitoSinIngreso) => r.proveedor?.nombre ?? '—' },
    { key: 'fecha', header: 'Fecha', render: (r: RemitoSinIngreso) => fechaCorta(r.fecha) },
    { key: 'ordenCompra', header: 'OC', render: (r: RemitoSinIngreso) => r.ordenCompra.numeroOrden || `OC #${r.ordenCompra.id}` },
    {
      key: 'situacion', header: 'Estado',
      help: 'Error: la recepción en el depósito falló. Quedó a medias: la carga se cortó sin terminar (por ejemplo, un reinicio del sistema).',
      render: (r: RemitoSinIngreso) => (r.situacion === 'error'
        ? <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Error</span>
        : <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Quedó a medias</span>),
    },
    {
      key: 'wmsError', header: 'Error', width: '22rem',
      render: (r: RemitoSinIngreso) => <span className="text-red-700 whitespace-normal break-words">{r.wmsError ?? '—'}</span>,
    },
    {
      key: 'recepcionesEnBorrador', header: 'Recepción en borrador',
      render: (r: RemitoSinIngreso) => (r.recepcionesEnBorrador.length > 0
        ? `Sí (${r.recepcionesEnBorrador.map(b => `#${b.id}`).join(', ')})`
        : 'No'),
    },
    {
      key: 'acciones', header: '', filterable: false, sortable: false,
      render: (r: RemitoSinIngreso) => (
        <Button
          size="sm"
          onClick={() => setAReintentar(r)}
          disabled={!puedeEditar || procesando}
          title={!puedeEditar ? SIN_PERMISO : undefined}
        >
          Reintentar ingreso
        </Button>
      ),
    },
  ];

  const columnasRecepciones = [
    { key: 'id', header: 'Recepción', render: (r: RecepcionEnBorrador) => `#${r.id}` },
    {
      key: 'remito', header: 'Remito',
      render: (r: RecepcionEnBorrador) => nombreRemito(r.remito?.numeroRemito ?? r.numeroRemito, r.remitoId),
    },
    { key: 'proveedorNombre', header: 'Proveedor', render: (r: RecepcionEnBorrador) => r.proveedorNombre ?? '—' },
    { key: 'numeroOrden', header: 'OC', render: (r: RecepcionEnBorrador) => r.numeroOrden ?? '—' },
    { key: 'fecha', header: 'Fecha', render: (r: RecepcionEnBorrador) => fechaCorta(r.fecha) },
    { key: 'estadoRemito', header: 'Stock del remito', render: (r: RecepcionEnBorrador) => estadoRemito(r) },
    {
      key: 'acciones', header: '', filterable: false, sortable: false,
      render: (r: RecepcionEnBorrador) => {
        const sincronizando = r.remito?.wmsStatus === 'syncing';
        return (
          <Button
            size="sm"
            variant="danger"
            onClick={() => setADescartar(r)}
            disabled={!puedeEditar || sincronizando || procesando}
            title={!puedeEditar ? SIN_PERMISO : sincronizando ? 'El remito se está sincronizando; probá de nuevo en unos segundos' : undefined}
          >
            Descartar
          </Button>
        );
      },
    },
  ];

  const borradoresAReintentar = aReintentar?.recepcionesEnBorrador ?? [];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Mantenimiento</h1>
        <p className="text-slate-500 text-sm mt-1">Corrección de datos que quedaron a medias</p>
      </div>

      {verRemitos && <>
      <Seccion
        id="mant-remitos"
        titulo="Remitos con OC que no ingresaron al stock"
        descripcion="La recepción en el depósito falló o quedó a medias, y la mercadería de estos remitos no sumó al stock. Reintentar ingreso la hace entrar una sola vez."
      >
        {remitos.error && <p className="text-sm text-red-600" role="alert">{remitos.error}</p>}
        <DataTable
          columns={columnasRemitos}
          data={remitos.data ?? []}
          loading={remitos.loading}
          emptyMessage="No hay remitos pendientes de ingresar al stock."
          title="Remitos sin ingreso de stock"
        />
      </Seccion>

      <Seccion
        id="mant-recepciones"
        titulo="Recepciones en borrador de remitos"
        descripcion="Recepciones del depósito que la sincronización de remitos dejó sin confirmar. No movieron stock; confirmarlas sumaría la mercadería otra vez, por eso sólo se pueden descartar."
      >
        {recepciones.error && <p className="text-sm text-red-600" role="alert">{recepciones.error}</p>}
        <DataTable
          columns={columnasRecepciones}
          data={recepciones.data ?? []}
          loading={recepciones.loading}
          emptyMessage="No hay recepciones en borrador de remitos."
          title="Recepciones en borrador de remitos"
        />
      </Seccion>
      </>}

      {verMotivos && (
        <Seccion
          id="mant-motivos"
          titulo="Motivos de movimientos de stock"
          descripcion="Catálogo de motivos de las bajas, ajustes y devoluciones que se cargan a mano en Movimientos de stock: cada uno fija el tipo de movimiento y a cargo de quién queda. Es obligatorio elegir uno al registrar. No se borran: se desactivan."
        >
          <MotivosMovimientoStock />
        </Seccion>
      )}

      {user?.role === 'admin' && (
        <Seccion
          id="mant-datos-prueba"
          titulo="Datos de PRUEBA"
          descripcion="Proveedores, productos y OC de prueba para practicar la carga de remitos en el sistema real. Todo lleva la palabra PRUEBA y se borra con un botón al terminar. Sólo para administradores."
        >
          <DatosPrueba />
        </Seccion>
      )}

      <Confirm
        open={aReintentar != null}
        title="Reintentar ingreso"
        message={aReintentar
          ? `La mercadería del remito ${nombreRemito(aReintentar.numeroRemito, aReintentar.id)} va a ingresar al depósito general: `
            + 'suma al Stock físico y al Stock disponible.'
            + (borradoresAReintentar.length > 0
              ? `\nSe descarta la recepción en borrador que quedó (${borradoresAReintentar.map(b => `#${b.id}`).join(', ')}).`
              : '')
            + '\nSi vuelve a fallar, el remito sigue en esta lista con el error nuevo y lo podés reintentar después.'
          : ''}
        confirmLabel="Reintentar"
        onConfirm={() => { void reintentar(); }}
        onCancel={() => setAReintentar(null)}
      />

      <Confirm
        open={aDescartar != null}
        title="Descartar recepción en borrador"
        message={aDescartar
          ? `La recepción #${aDescartar.id} no movió stock. Se descarta para que nadie la confirme: confirmarla sumaría la mercadería otra vez.`
            + '\nNo se puede deshacer.'
          : ''}
        confirmLabel="Descartar recepción"
        danger
        onConfirm={() => { void descartar(); }}
        onCancel={() => setADescartar(null)}
      />
    </div>
  );
}
